import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, ChevronRight, MapPin, Loader } from 'lucide-react';
import { usePropertyDetail } from '../hooks/usePropertyDetail';
import LazyImage from '../components/LazyImage';
import ImageLightbox from '../components/ImageLightbox';
import BookingModal from '../components/BookingModal';

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: queryData, isLoading, error } = usePropertyDetail(id);
  
  const property = queryData?.property;
  const bookedDates = queryData?.bookedDates || [];
  
  const [mainImage, setMainImage] = useState('');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Update main image when property loads
  React.useEffect(() => {
    if (property?.images?.[0]?.url) {
      setMainImage(property.images[0].url);
    }
  }, [property]);

  if (isLoading) {
    return (
      <div className="pt-32 pb-24 px-6 flex justify-center items-center min-h-screen">
        <Loader size={40} className="text-prose-brand animate-spin" />
      </div>
    );
  }

  if (!property || error) {
    return (
      <div className="pt-32 pb-24 px-6 text-center">
        <h1 className="text-4xl font-display font-bold mb-4">Property Not Found</h1>
        <button 
          onClick={() => navigate('/properties')}
          className="bg-prose-brand text-white px-8 py-3 rounded-lg font-semibold"
        >
          Back to Properties
        </button>
      </div>
    );
  }

  // Calendar helper functions
  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  const isDateBooked = (dateStr) => bookedDates.includes(dateStr);

  const getDaysArray = (month) => {
    const daysInMonth = getDaysInMonth(month);
    const firstDay = getFirstDayOfMonth(month);
    const days = [];
    
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      days.push(dateStr);
    }
    return days;
  };

  const isDateDisabled = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  const plainDescription = property.description
    ? property.description.replace(/\s+/g, ' ').trim().slice(0, 155)
    : `${property.title} - a ${property.bedrooms}-bedroom ${property.type} available to book with ProjectProse.`;
  const location = property.address?.split('\n')[0] || 'the UK';

  return (
    <div className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-3 sm:px-4 md:px-6 bg-white w-full">
        <Helmet>
          <title>{`${property.title} | ${location} | ProjectProse`}</title>
          <meta name="description" content={plainDescription} />
          <link rel="canonical" href={`https://www.project-prose.com/property/${id}`} />
        </Helmet>
        <div className="max-w-7xl mx-auto w-full">
        <button 
          onClick={() => navigate('/properties')}
          className="flex items-center gap-2 text-prose-brand mb-6 sm:mb-8 font-semibold hover:gap-4 hover:-translate-x-1 transition-all duration-300 text-xs sm:text-sm md:text-base"
        >
          <ChevronLeft size={18} /> Back to Properties
        </button>

        <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8 w-full">
          <div className="flex-1 lg:flex-[7] space-y-8 min-w-0">
            <div>
              <div className="flex flex-row flex-wrap items-end justify-between gap-3 sm:gap-4 mb-4">
                <h1 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold text-prose-black flex-1">{property.title}</h1>
                <div className="whitespace-nowrap">
                  <p className="text-xs sm:text-sm text-gray-600 mb-1">Price per night</p>
                  <p className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold text-prose-gold">£{parseInt(property.price).toLocaleString()}</p>
                </div>
              </div>
              <p className="flex items-center gap-2 text-prose-brand font-semibold text-xs sm:text-sm md:text-lg">
                <MapPin size={18} /> {property.address?.split('\n')[0] || 'Location'}
              </p>
            </div>

            <div className="space-y-4">
              <div 
                className="relative overflow-hidden rounded-xl bg-gray-100 aspect-video cursor-pointer group"
                onClick={() => {
                  const index = property.images?.findIndex(img => img.url === mainImage) || 0;
                  setLightboxIndex(index >= 0 ? index : 0);
                  setShowLightbox(true);
                }}
              >
                <img 
                  src={mainImage || 'https://via.placeholder.com/800x600.png'} 
                  alt={property.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/80 text-prose-brand px-4 py-2 rounded-lg font-semibold text-sm">
                    Click to view
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                {property.images?.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setMainImage(img.url);
                      setLightboxIndex(idx);
                    }}
                    onDoubleClick={() => {
                      setLightboxIndex(idx);
                      setShowLightbox(true);
                    }}
                    className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all hover:scale-110 group cursor-pointer ${
                      mainImage === img.url ? 'border-prose-brand' : 'border-gray-200'
                    }`}
                    title="Click to select, double-click to expand"
                  >
                    <LazyImage 
                      src={img.url} 
                      alt={`Thumbnail ${idx}`} 
                      className="w-full h-full group-hover:scale-110 transition-transform duration-300"
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 p-4 sm:p-8 rounded-xl">
              <h3 className="text-base sm:text-xl md:text-2xl font-display font-bold text-prose-black mb-4">About This Property</h3>
              <p className="text-gray-700 leading-relaxed text-justify text-xs sm:text-sm md:text-base">{property.description}</p>
            </div>

            <div className="space-y-6 bg-gray-50 p-4 sm:p-8 rounded-xl">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                <div className="bg-white p-3 sm:p-4 rounded-lg">
                  <p className="text-gray-600 text-xs sm:text-sm mb-1">Address</p>
                  <p className="font-semibold text-xs sm:text-sm md:text-base">{property.address?.split('\n')[0]}</p>
                </div>
                <div className="bg-white p-3 sm:p-4 rounded-lg">
                  <p className="text-gray-600 text-xs sm:text-sm mb-1">Type</p>
                  <p className="text-lg sm:text-2xl font-display font-bold text-prose-gold">{property.type}</p>
                </div>
                <div className="bg-white p-3 sm:p-4 rounded-lg">
                  <p className="text-gray-600 text-xs sm:text-sm mb-1">Size</p>
                  <p className="text-base sm:text-lg font-bold text-prose-brand">{property.size}</p>
                </div>
                <div className="bg-white p-3 sm:p-4 rounded-lg">
                  <p className="text-gray-600 text-xs sm:text-sm mb-1">Bedrooms</p>
                  <p className="text-lg sm:text-xl font-bold text-prose-black">{property.bedrooms}</p>
                </div>
                <div className="bg-white p-3 sm:p-4 rounded-lg">
                  <p className="text-gray-600 text-xs sm:text-sm mb-1">Bathrooms</p>
                  <p className="text-lg sm:text-xl font-bold text-prose-black">{property.bathrooms}</p>
                </div>
                <div className="bg-white p-3 sm:p-4 rounded-lg">
                  <p className="text-gray-600 text-xs sm:text-sm mb-1">Status</p>
                  <p className="font-semibold text-xs sm:text-sm text-prose-brand">{property.status}</p>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-4">
              <h3 className="text-lg sm:text-2xl font-display font-bold text-prose-black">Features</h3>
              <div className="bg-prose-gold/10 p-4 sm:p-6 rounded-xl space-y-3">
                {property.features?.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-prose-gold rounded-full"></div>
                    <p className="text-prose-black">{typeof feature === 'string' ? feature : feature.feature}</p>
                  </div>
                ))}
              </div>
            </div>

            {property.additional_notes && (
              <div className="space-y-4">
                <h3 className="text-lg sm:text-2xl font-display font-bold text-prose-black">Additional Notes</h3>
                <div className="bg-gray-50 p-4 sm:p-6 rounded-xl">
                  <p className="text-gray-700 leading-relaxed text-justify">{property.additional_notes}</p>
                </div>
              </div>
            )}

          </div>

          <div className="w-full lg:flex-[3] flex-shrink-0">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="bg-white border-2 border-gray-200 p-4 sm:p-6 rounded-xl">
                <h4 className="font-display font-bold text-base sm:text-lg mb-4 text-prose-black">Availability</h4>
                
                <div className="flex justify-between items-center mb-4">
                  <p className="text-sm font-semibold text-gray-600">
                    {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                      className="p-2 hover:bg-gray-100 rounded transition-all"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                      className="p-2 hover:bg-gray-100 rounded transition-all"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {getDaysArray(currentMonth).map((date, idx) => {
                    if (!date) return <div key={`empty-${idx}`}></div>;
                    const booked = isDateBooked(date);
                    const disabled = isDateDisabled(date);

                    return (
                      <div
                        key={date}
                        className={`aspect-square text-xs font-semibold rounded flex items-center justify-center ${
                          booked
                            ? 'bg-red-200 text-red-700 font-bold'
                            : disabled
                            ? 'bg-gray-200 text-gray-500'
                            : 'bg-green-100 text-green-700'
                        }`}
                        title={booked ? 'Booked' : 'Available'}
                      >
                        {new Date(date).getDate()}
                      </div>
                    );
                  })}
                </div>

                <div className="bg-gray-50 p-3 rounded-lg mt-4 space-y-2">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Legend</p>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="inline-block w-3 h-3 bg-green-100 rounded border border-green-300"></span>
                    Available
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="inline-block w-3 h-3 bg-red-200 rounded"></span>
                    Booked
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="inline-block w-3 h-3 bg-gray-200 rounded"></span>
                    Past Dates
                  </div>
                  <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-200">
                    Click "Book Now" to select dates and reserve
                  </p>
                </div>
              </div>

              <button 
                onClick={() => setShowBookingModal(true)}
                className="w-full bg-prose-gold text-white font-semibold py-3 sm:py-4 rounded-lg hover:bg-prose-gold/90 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 text-base sm:text-lg"
              >
                Book Now
              </button>

              <div className="bg-gray-50 p-4 sm:p-6 rounded-xl">
                <h4 className="font-semibold mb-3 text-prose-black text-sm sm:text-base">Questions?</h4>
                <Link to="/contact" className="block w-full bg-prose-brand text-white py-2 sm:py-3 rounded-lg font-semibold hover:bg-prose-brand/90 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 text-center text-sm sm:text-base">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showBookingModal && (
        <BookingModal 
          property={{ ...property, bookedDates }} 
          onClose={() => setShowBookingModal(false)}
        />
      )}

      {showLightbox && (
        <ImageLightbox 
          images={property.images || []} 
          initialIndex={lightboxIndex}
          onClose={() => setShowLightbox(false)}
        />
      )}
    </div>
  );
};

export default PropertyDetail;
